import { makeAutoObservable, runInAction, toJS } from 'mobx'
import { localStore, trace, ddayjs } from '@dt/fe-utils'
import { filter, indexOf, each, keyBy, find, findIndex, cloneDeep } from '@dt/fe-utils/dist/lodash'
import { fmtMktMessage, toast, md5, miniAppRouteTo } from 'common/utils'
import { memberMobx, memberCardMobx } from '../rootMobx'
import { mktConfigMobx } from '@mobxStore'
import { restaurantCenter, RESTAURANT_EVENT } from 'restaurant'
import Config from 'common/config/config'
import { MemberApi, ThirdAuthAPI, ActivityAPI } from '@api/index'
import { checkExpire, getVisitorInfo, setVisitorInfo, genID, getPlatform } from '@utils'
import { mpc } from '@dt/mmg-sdk'
import i18n from 'i18next'

const { isWeixin, isWeixinMiniProgram } = getPlatform()

const defaultMemberInfo: IMemberInfo = {
  brandId: 0,
  memberId: 0,
  visitorId: '',
  memberName: '',
  gender: null,
  avatarUrl: '',
  birthday: '',
  countryCode: 86,
  mobile: '',
  email: '',
  nickName: '',
  openId: '',
  gOpenId: '',
  type: 'member',
  memberIds: [],
  extra: {
    displayMode: 'waterfall',
    posPayNeedPwd: false
  },
  gAppId: '',
  hasLoginPwd: false
}

const defaultMemberExtra: IMemberExtra = {
  displayMode: '',
  posPayNeedPwd: false,
}

interface IMemberCard {
  cardId: number;
  cardName: string;
  discountRate?: number; // 打几折
  discountType: string;
  discountValue: number;
  memberCardId: number;
  storeIds: number[]; // 适用的门店ID数组
  isIncludeModifier: boolean; // 是否包含加料
  productRelation: string; // 餐品关系
  products: any[]; // 餐品数组
  // 积分数据
  integral: number; // 积分
  integralMoney: number; // 多少积分
  integralCostMoney: number; // 抵扣多少元
  isIntegralMoney: boolean; // 是否允许积分抵扣
  integralMoneyMax: number; // 最高抵扣金额
  integralMoneyMaxType: string; // 最高抵扣金额类型,discountFee, discountRate
  isAllStore: boolean;
  isIntegral: boolean; // 能不能积积分
  costMoney: number; // 花多少钱
  getIntegral: number; // 获得多少积分
  extra?: {
    noBalanceNoDiscount: boolean; // 余额为0不参与折扣
    mallNoBalanceNoDiscount: boolean;
    balanceDiscountRule: string; // 会员卡权益的享受规则
  };
  balance: number; // 余额
  mallCostMoney: number;
  mallGetIntegral: number;
  mallProductRelation: string;

  mallIntegralMoney: number; // 多少积分
  mallIntegralCostMoney: number; // 抵扣多少元
  mallIntegralMoneyMax: number; // 最高抵扣金额
  mallIntegralMoneyMaxType: string; // 最高抵扣金额类型,discountFee, discountRate
  mallDiscountType: string; // 商城的
  mallDiscountValue: number; // 商城的
  mallProducts: any[]; // 商城产品
  isIncludeCombo: boolean; // 套餐是否参与优惠
  expireTime: string; // 过期时间
  recharges: any[];
  publishType: string; // 发行方式
}

interface IMember {
  memberId: number; // 会员ID
  cards: IMemberCard[];
  coupons: any[];
  type: string; // 会员类型
  noPurchasedCards: IMemberCard[];
  couponsConfigMap: any;
}

const getDefaultMemberInfo = () => Object.assign({}, defaultMemberInfo)

class MemberStore {
  constructor () {
    makeAutoObservable(this, {}, { autoBind: true })
    restaurantCenter.on(RESTAURANT_EVENT.STORE_CHANGED, (info: IStore) => {
      // console.log('mkt_store_change_响应:', info.storeId, this._storeId)
      if (info.storeId !== this.storeId) {
        this._storeId = info.storeId
        this.cardInfo = this.getBestCard(this.cardListForPay)
        this.mpcSetMemberInfo(this.memberInfo, this.cardInfo, this.couponList, this.couponsConfigMap)
      }
    })
  }
  mpc: any = mpc
  // 品牌配置
  brandConfig: IMktBrndConfig = {
    discountCalTime: '',
    isSelectStoreWhenCz: false,
    isSelectStoreWhenBuyCard: false,
    isSelectStoreWhenRedeemCoupon: false,
    isShowRedeemMoneySale: false,
    isShowRedeemIntegralSale: false,
    openIdSignMember: false,
    shareImg: '',
    couponExpireRemind: null,
    isAutoAcquireCoupon: null,
    commentSetting: null,
    memberCardCzSetting: null,
    giftcardCzSetting: null,
    mobileGiftcardPayMustBind: false,
  }
  // 会员信息
  memberInfo: IMemberInfo = getDefaultMemberInfo();
  // 会员其他配置
  memberExtra: IMemberExtra = defaultMemberExtra;
  giftCardList: IMemberGiftCardInfo[] = [];
  cardList: IMemberCardInfo[] = [];
  couponList: any[] = [];
  couponsConfigMap: any = {};
  // 未领取
  isUnacquiredInit: boolean = false;
  unacquiredCoupons: any[] = [];
  giveLogs: any[] = [];
  cardInfo: IMemberCardInfo = null;
  mallCardInfo: IMemberCardInfo = null;
  operaAddress: any = null;
  addressList: any[] = [];
  openState: any = {}
  noPurchasedCards: IMemberCard[];
  hasShowLvInfo: boolean = false;
  updateHasShowLvInfo (data?: boolean) {
    this.hasShowLvInfo = !!data
  }
  setOperaAddress (info: any) {
    this.operaAddress = info
  }
  setCardInfo (info: any) {
    this.cardInfo = info
    this.mpcSetMemberInfo(this.memberInfo, info, this.couponList, this.couponsConfigMap)
  }
  setMallCardInfo (info: any) {
    this.mallCardInfo = info
    this.mpcSetMemberInfo(this.memberInfo, info, this.couponList, this.couponsConfigMap)
  }
  getDefaultCardTop = (list: IMemberCardInfo[]) => {
    let newList: IMemberCardInfo[] = cloneDeep(list)
    const hasItem: number = findIndex(newList, (m: IMemberCardInfo) => m.isDefault)
    if (hasItem > 0) {
      const curCard = newList.splice(hasItem, 1)
      newList = curCard.concat(newList)
    }
    return newList
  }
  getBestCard = (list: any[], isMall?: boolean) => {
    const cardList = list.filter((i: any) => {
      const canStore = i.card.isAllStore || (i.card.storeIds && i.card.storeIds.length && indexOf(i.card.storeIds, this.storeId) > -1)
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      if (isMall) {
        return i.status === 'enable' && !isExpired
      } else {
        return canStore && i.status === 'enable' && !isExpired
      }
    })
    let defaultCard: any
    let memberPriceCard: any
    let discountCard: any
    let paidCard: any // 付费卡
    each(cardList, (card: any) => {
      if (card.isDefault) {
        defaultCard = card
      }
      if (card?.card?.type === 'paid') {
        paidCard = card
      }
      if (isMall) {
        if (!discountCard && card.card.mallDiscountValue !== 1) {
          discountCard = card
        }
        if (discountCard && discountCard.card.mallDiscountValue > card.card.mallDiscountValue) {
          discountCard = card
        }
        if (card.card.mallDiscountType === 'memberPrice') {
          memberPriceCard = card
        }
      } else {
        if (!discountCard && card.card.discountValue !== 1) {
          discountCard = card
        }
        if (discountCard && discountCard.card.discountValue > card.card.discountValue) {
          discountCard = card
        }
        if (card.card.discountType === 'memberPrice') {
          memberPriceCard = card
        }
      }
    })

    const firstCard = cardList.length > 0 ? cardList[0] : undefined
    return defaultCard || discountCard || memberPriceCard || paidCard || firstCard
  }
  getMyCoupon (list: any[]) {
    const preList: any[] = []
    const lastList: any[] = []
    each(list, item => {
      if (item.status === 'enable') {
        preList.push(item)
      } else if (item.status === 'disable') {
        lastList.push(item)
      }
    })
    return preList.concat(lastList)
  }
  getPayCardList = () => {
    let cards = toJS(this.cardList)
    return filter(cards, (i: any) => {
      const canStore = !this.storeId || i.card.isAllStore || (i.card.storeIds && i.card.storeIds.length && indexOf(i.card.storeIds, this.storeId) > -1)
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      return canStore && i.status === 'enable'
    })
  }
  getMallPayCardList = () => {
    let cards = toJS(this.cardList)
    return filter(cards, (i: any) => {
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      return i.status === 'enable'
    })
  }
  // 充值并支付 过滤后的会员卡列表
  _memCardList: any[] = []
  // 品牌Id
  _brandId = 0
  _storeId = 0
  _token = ''
  _isNewMember = false
  // 门店Id
  get storeId () {
    return this._storeId
  }
  get brandId () {
    return this._brandId
  }
  // 用户相关 Key
  get tokenKey () {
    return `ls.token${window.appEnv}.${this._brandId || ''}`
  }
  get keepSignKey () {
    return `ls.keepSign.${this._brandId || ''}`
  }
  get visitorKey () {
    return `ls.visitor.${this._brandId || ''}`
  }
  get thirdAuthInfoKey () {
    return `ls.thirdAuthInfo.${this._brandId || ''}`
  }
  get bindGiftCardKey () {
    return `ls.bindGiftCardInfo.${this._brandId || ''}`
  }
  get receiveCouponKey () {
    return `ls.receiveCouponsInfo.${this._brandId || ''}`
  }
  get shareInfoKey () {
    return `ls.shareInfo.${this._brandId || ''}`
  }
  get reloadNumKey () {
    return `ls.reloadNum.${this._brandId || ''}`
  }
  get consumeCouponsKey () {
    return `ls.consumeCouponsKey.${this._brandId || ''}`
  }

  get miniOpenLinkKey () {
    return `ls.miniOpenLink.${this._brandId || ''}`
  }

  get token () {
    return this._token
  }
  get isLogin () {
    return !!this._token && this.memberInfo.memberId !== 0
  }
  get isMember () {
    return !!this._token && this.memberInfo.memberId !== 0 && this.memberInfo.type === 'member'
  }
  set isNewMember (bool: boolean) {
    this._isNewMember = bool
  }
  get isNewMember () {
    return this._isNewMember
  }

  // 三方授权信息
  get thirdAuthInfo () {
    return localStorage.getItem(this.thirdAuthInfoKey)
  }
  // 支付会员卡信息
  get cardInfoForPay (): IMemberCardInfo {
    return this.cardInfo || this.getPayCardList()[0] || undefined
  }
  get mallCardInfoForPay (): IMemberCardInfo {
    return this.mallCardInfo || this.getMallPayCardList()[0] || undefined
  }
  // 支付用会员卡列表
  get cardListForPay (): IMemberCardInfo[] {
    return this.getPayCardList()
  }
  // 商城支付用会员卡列表
  get cardListForMall (): IMemberCardInfo[] {
    return this.getMallPayCardList()
  }
  // 支付用礼品卡
  get giftCardListForPay (): IMemberGiftCardInfo[] {
    let giftCards = toJS(this.giftCardList)
    return filter(giftCards, (i: any) => {
      const canStore = !this.storeId || i.giftcard.isAllStore ||
        (i.giftcard.storeIds &&
          i.giftcard.storeIds.length &&
          indexOf(i.giftcard.storeIds, this.storeId) > -1)
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs().format('YYYY-MM-DD HH:mm:ss')) : false
      return canStore && i.status === 'enable' && !isExpired
    })
  }
  // 可使用优惠券
  get couponListForUse () {
    let coupons = toJS(this.couponList)
    return filter(coupons, (i: any) => (i.status === 'enable'))
  }
  // 停用优惠券
  get couponListForDisable () {
    let coupons = toJS(this.couponList)
    return filter(coupons, (i: any) => (i.status === 'disable'))
  }
  // 会员页卡列表
  get cardListForMy (): IMemberCardInfo[] {
    let cards = toJS(this.cardList)
    return filter(cards, (i: any) => {
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      return i.status === 'enable' && !isExpired
    })
  }
  // 会员卡失效卡列表
  get cardListForExp (): IMemberCardInfo[] {
    let cards = toJS(this.cardList)
    return filter(cards, (i: any) => {
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      return i.status === 'disable' || isExpired
    })
  }
  get cardListMap () {
    let cards = toJS(this.cardList)
    const listMap = {}
    each(cards, (i: any) => {
      listMap[i.cardId] = i
    })
    return listMap
  }
  // 可用礼品卡
  get giftListForMy (): IMemberGiftCardInfo[] {
    let cards = toJS(this.giftCardList)
    return filter(cards, (i: any) => {
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      return i.status === 'enable' && !isExpired && (i.balance > 0 || i.giftcard.allowAgainLoad)
    })
  }
  // 不可用礼品卡
  get giftListForExp (): IMemberGiftCardInfo[] {
    let cards = toJS(this.giftCardList)
    return filter(cards, (i: any) => {
      const isExpired: boolean = i.expireTime ? checkExpire(i.expireTime, ddayjs('brand').format('YYYY-MM-DD HH:mm:ss')) : false
      return i.status === 'disable' || isExpired || (!i.giftcard.allowAgainLoad && !i.balance)
    })
  }
  get giftCardListMap () {
    let giftCards = toJS(this.giftCardList)
    const listMap = {}
    each(giftCards, (i: any) => {
      listMap[i.giftcardId] = i
    })
    return listMap
  }
  /**
   * 会员登录
   * @param params-参数
   * @param noPrompt-不需要提示
   */
  async login (type: 'normal' | 'facebook' | 'google' | 'googleAuth' | 'wechatPage' | 'token', params: any, noPrompt?: boolean, isMd5: boolean = true) {
    Object.assign(params, {
      storeId: this.storeId
    })
    params.email && (params.email = params.email.trim())
    params.mobile && (params.mobile = params.mobile.trim())
    params.captcha && (params.captcha = params.captcha.trim())
    params.loginPwd && (params.loginPwd = isMd5 ? md5(params.loginPwd.trim()) : params.loginPwd.trim())
    params.gOpenId && (params.gOpenId = params.gOpenId.trim())
    if (Config.shareInfo) {
      const shareInfoObj: any = JSON.parse(decodeURIComponent(Config.shareInfo))
      params.invitation = shareInfoObj
    }
    if (Config.posLoginInfo) {
      const obj: any = JSON.parse(decodeURIComponent(Config.posLoginInfo))
      params.deviceInfo = {
        deviceId: obj.storeDeviceId,
        timestamp: obj.time
      }
    }
    let res: IMemberInfo
    let beneRes: any
    let cardInfo: any
    let mallCardInfo: any
    try {
      if (type === 'normal') {
        res = await MemberApi.login(params)
      } else if (type === 'facebook') {
        res = await MemberApi.fbLogin(params)
      } else if (type === 'google') {
        res = await MemberApi.ggLogin(params)
      } else if (type === 'googleAuth') {
        res = await MemberApi.ggAuthLogin(params)
      } else if (type === 'wechatPage') {
        res = await ThirdAuthAPI.wechatLogin(params)
      } else if (type === 'token') {
        res = await MemberApi.getMemeberInfo()
        res.token = params.token
      }
      await Config.setToken(res.token)
      if (res) (this._token = res.token)
      if (res) {
        beneRes = await MemberApi.getMemeberBenefits()
        runInAction(() => {
          beneRes && (this.noPurchasedCards = beneRes.noPurchasedCards)
        })
      }
      if (beneRes) {
        cardInfo = this.getBestCard(beneRes.memberCards)
        mallCardInfo = this.getBestCard(beneRes.memberCards, true)
      }
      const infoRes = await MemberApi.getMemeberInfo()
      res = Object.assign(res, infoRes)
      res && beneRes && await this.mpcSetMemberInfo(res, this.mpc.eatType === 'mall' ? mallCardInfo : cardInfo, beneRes.coupons, beneRes.couponsConfigMap)
    } catch (error) {
      trace.traceLog({
        name: '会员登录: => login 登录失败',
        message: { error, params, type }
      })
      if (!noPrompt) toast({ message: i18n.t('mkt.member.loginFailTip') })
    }
    runInAction(async () => {
      res && (this._isNewMember = res.isNewMember)
      res && this.setMemberInfo(res)
      res && this.clearShareInfo()
      beneRes && (this.cardInfo = cardInfo)
      beneRes && (this.mallCardInfo = mallCardInfo)
      beneRes && (this.cardList = this.getDefaultCardTop(beneRes.memberCards))
      beneRes && (this.couponList = this.getMyCoupon(beneRes.coupons))
      beneRes && (this.giftCardList = beneRes.giftCards)
    })
    return res
  }
  /**
   * 创建虚拟用户
   * @param para
   */
  async virtualMember (para?: {
    nickName: string;
  }) {
    const params: any = {
      storeId: this.storeId,
      ...para,
    }
    let res: any
    try {
      res = await MemberApi.virtual(params)
      if (res) {
        await Config.setToken(res.token)
        this.memberInfo = res
      }
    } catch (error) { }
    runInAction(() => {
      res && (this._token = res.token)
      res && this.mpcSetMemberInfo(res, null, [], {})
      res && this.getMemberInfo()
    })
    return res.memberId || 0
  }
  /** 是否在请求权益 */
  requestBenefits = false;
  /** 权益缓存数据 */
  requestBenefitResultCache = null;
  /**
  * 刷新会员权益
  */
  async refreshMemeberBenefits () {
    if (!memberMobx.isLogin) return
    if (this.requestBenefits) {
      return
    }
    this.requestBenefits = true
    if (this.requestBenefitResultCache) {
      this.requestBenefits = false
      this.initBeneRes(this.requestBenefitResultCache)
      return this.requestBenefitResultCache
    }
    let beneRes: any
    try {
      beneRes = await MemberApi.getMemeberBenefits()
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      this.initBeneRes(beneRes)
    })
    this.requestBenefits = false
    this.requestBenefitResultCache = beneRes
    this.clearRequestBenefitResult()
    return beneRes
  }
  /** 统一处理会员权益数据，缓存过来也要处理 */
  initBeneRes (beneRes) {
    try {
      beneRes && (this.noPurchasedCards = beneRes.noPurchasedCards)
      beneRes && (this.cardList = this.getDefaultCardTop(beneRes.memberCards))
      beneRes && (this.couponList = this.getMyCoupon(beneRes.coupons))
      if (this.brandConfig.isAutoAcquireCoupon) {
        beneRes && (this.getUnacquiredCoupons(true))
      }
      if (!this.cardInfo) {
        beneRes && (this.cardInfo = this.getBestCard(beneRes.memberCards))
      } else {
        beneRes && (this.cardInfo = beneRes.memberCards.filter((item: any) => item.memberCardId === this.cardInfo.memberCardId)[0])
      }
      if (!this.mallCardInfo) {
        beneRes && (this.mallCardInfo = this.getBestCard(beneRes.memberCards, true))
      } else {
        beneRes && (this.mallCardInfo = beneRes.memberCards.filter((item: any) => item.memberCardId === this.mallCardInfo.memberCardId)[0])
      }
      beneRes && (this.couponsConfigMap = beneRes.couponsConfigMap)
      beneRes && this.mpcSetMemberInfo(this.memberInfo, this.mpc.eatType === 'mall' ? this.mallCardInfo : this.cardInfo, beneRes.coupons, beneRes.couponsConfigMap)
      beneRes && (this.giftCardList = beneRes.giftCards)
    } catch (error) {
    }
  }
  clearRequestBenefitResult () {
    setTimeout(() => {
      // 缓存3秒数据
      this.requestBenefitResultCache = null
    }, 3000)
  }
  /**
  * 检查会员卡可充值档位
  */
  async checkMcReStall (memberCardId: number, originalPrice: number, orderTip: number) {
    const cardInfo: any = find(this.cardList, (c: any) => c.memberCardId === memberCardId)
    console.log('判断这里的卡🧶', cardInfo)
    if (cardInfo && cardInfo.card.allowAgainLoad && cardInfo.card.recharges && cardInfo.card.recharges.length > 0) {
      const resArr: any[] = []
      each(cardInfo.card.recharges, (rItem: any) => {
        const total: number = cardInfo.balance + rItem.money + rItem.moneyGive
        const pureAmount = cardInfo.balanceRecharge + rItem.money
        console.log(cardInfo.balance, rItem.money, rItem.moneyGive, 'cardInfo.balance + rItem.money + rItem.moneyGive', total, 'total', originalPrice, 'originalPrice')
        if (total >= originalPrice && pureAmount >= orderTip) {
          resArr.push(rItem)
        }
      })
      this.memberCardList(resArr.length > 0 ? resArr : [])
      return resArr.length > 0 ? resArr : null
    } else {
      return null
    }
  }

  // 修改充值并支付的会员卡列表
  memberCardList (dataList: any[]) {
    this._memCardList = dataList
  }
  /**
   * @param para /
   * @returns
   */
  async giveCoupon (para: {
    memberCouponIds: any[];
    receiverMobile?: string;
    countryCode?: string;
    receiverEmail?: string;
    giverName?: string;
    receiverName?: string;
    message?: string;
  }) {
    let res: any
    try {
      res = await MemberApi.giveCoupon(para)
    } catch (error) { }
    runInAction(() => {
      res && this.getMemberCoupons()
    })
    return res
  }
  /**
 * 转换会员信息
 * @param info
 * @returns
 */
  private converMemberInfo (info: any, visitor?: any) {
    const visitorInfo: any = visitor || getVisitorInfo()
    return {
      ...info,
      visitorId: visitorInfo ? visitorInfo.id : '',
    }
  }
  /**
 * 转换会员信息
 * @param info
 * @returns
 */
  private setMemberInfo (member: any) {
    const visitorInfo: any = getVisitorInfo()
    let localVisitor: any
    if (!visitorInfo) {
      localVisitor = {
        id: genID(),
        name: member.nickName,
        avatar: member.avatarUrl,
        isSave: 1,
      }
      setVisitorInfo(localVisitor)
    }
    this.memberInfo = this.converMemberInfo(member, localVisitor)
    this.memberExtra = {
      ...defaultMemberExtra,
      ...member.extra,
    }
  }
  // 清除会员相关存储信息
  private clearShareInfo () {
    localStore.remove(this.shareInfoKey)
  }
  // 置入营销
  private mpcSetMemberInfo (memberInfo: IMemberInfo, cardInfo: IMemberCardInfo, couponList: any[], couponsConfigMap: any) {
    // mpc.clearMemberInfo()
    const mpcInfo: IMember = {
      memberId: memberInfo.memberId,
      cards: cardInfo ? [
        {
          cardId: cardInfo.card.cardId,
          cardName: cardInfo.card.cardName,
          discountValue: cardInfo.card.discountValue,
          discountType: cardInfo.card.discountType,
          memberCardId: cardInfo.memberCardId || 0,
          storeIds: cardInfo.card.storeIds,
          isIncludeModifier: cardInfo.card.isIncludeModifier,
          productRelation: cardInfo.card.productRelation,
          mallProductRelation: cardInfo.card.mallProductRelation,
          products: cardInfo.card.products,
          // 积分数据
          integral: cardInfo.integral,
          integralMoney: cardInfo.card.integralMoney,
          integralCostMoney: cardInfo.card.integralCostMoney,
          isIntegralMoney: cardInfo.card.isIntegralMoney,
          integralMoneyMax: cardInfo.card.integralMoneyMax,
          integralMoneyMaxType: cardInfo.card.integralMoneyMaxType,
          isAllStore: cardInfo.card.isAllStore,
          // 获取积分
          isIntegral: cardInfo.card.isIntegral, // 是否能积分
          costMoney: cardInfo.card.money, // 花多少钱
          getIntegral: cardInfo.card.integral, // 算多少积分
          extra: {
            noBalanceNoDiscount: cardInfo.card.extra.noBalanceNoDiscount,
            mallNoBalanceNoDiscount: cardInfo.card.extra.mallNoBalanceNoDiscount,
            balanceDiscountRule: cardInfo.card?.extra?.balanceDiscountRule
          },
          balance: cardInfo.balance,
          mallCostMoney: cardInfo.card.mallMoney, // 花多少钱
          mallGetIntegral: cardInfo.card.mallIntegral, // 算多少积分
          mallIntegralMoney: cardInfo.card.mallIntegralMoney,
          mallIntegralCostMoney: cardInfo.card.mallIntegralCostMoney,
          mallIntegralMoneyMax: cardInfo.card.mallIntegralMoneyMax,
          mallIntegralMoneyMaxType: cardInfo.card.mallIntegralMoneyMaxType,
          mallDiscountValue: cardInfo.card.mallDiscountValue,
          mallDiscountType: cardInfo.card.mallDiscountType,
          mallProducts: cardInfo.card.mallProducts,
          isIncludeCombo: cardInfo.card.isIncludeCombo,
          expireTime: cardInfo.expireTime,
          recharges: cardInfo.card.recharges,
          publishType: cardInfo.card.publishType
        }
      ] : [],
      coupons: couponList,
      type: memberInfo.type,
      noPurchasedCards: this.noPurchasedCards,
      couponsConfigMap: couponsConfigMap
    }
    if (this.token) mpc.setMebToken(this.token)
    return mpc.setMemberInfo(mpcInfo as any)
  }
  public initMpc () {
    this.refreshMemeberBenefits()
  }
  // 获取会员信息
  async getMemberInfo () {
    if (!this.token) return
    let res: IMemberInfo
    try {
      res = await MemberApi.getMemeberInfo()
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && this.setMemberInfo(res)
    })
    return res
  }
  // 更新会员信息
  async updateMemberInfo (params: any) {
    if (!this.token) {
      return
    }
    let res: any
    try {
      res = await MemberApi.updateMemeber(params)
      await this.getMemberInfo()
    } catch (error) { }
    return res
  }
  // 发送验证码
  async sendCode (params: {
    type: 'login' | 'resetPwd' | 'bind' | 'register';
    countryCode: number;
    mobile: string;
    email: string;
  }) {
    let res: any
    try {
      res = await MemberApi.sendCode(params)
      !res && (res = true)
    } catch (error) { }
    return res
  }
  // 获取会员礼品卡
  async getMemberGiftCards () {
    const memberId = memberMobx.memberInfo?.memberId
    if (!memberId) {
      trace.traceLog(
        {
          name: '会员: => 获取礼品卡异常',
          message: { brandId: Config.BRANDID, memberId }
        }
      )
    }
    const params = {
      memberId: memberId,
      type: 'giftcard',
    }
    let res: any[]
    try {
      res = await MemberApi.getMemeberCards(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.giftCardList = res)
    })
    return res
  }
  // 获取会员礼品卡详情
  async getMemberGiftCardDetail (para: {
    cardNo?: string;
    memberCardId?: number;
  }) {
    const params = {
      type: 'giftcard',
      ...para,
    }
    params.cardNo && (params.cardNo = params.cardNo.replace(/\s/g, ''))
    let res: any
    try {
      res = await MemberApi.getMemeberCardDetail(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    return res
  }

  // 获取会员会员卡
  async getMemberCards () {
    const memberId = memberMobx.memberInfo?.memberId
    if (!memberId) {
      trace.traceLog(
        {
          name: '会员: => 获取会员卡异常',
          message: { brandId: Config.BRANDID, memberId }
        }
      )
    }
    const params = {
      memberId: memberId,
      type: 'card',
    }
    let res: IMemberCardInfo[]
    try {
      res = await MemberApi.getMemeberCards(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.cardList = this.getDefaultCardTop(res))
    })
    return res?.length ? this.getDefaultCardTop(res) : res
  }
  // 获取会员会员卡详情
  async getMemberCardDetail (para: any) {
    const params = {
      type: 'card',
      ...para,
    }
    params.cardNo && (params.cardNo = params.cardNo.replace(/\s/g, ''))
    let res: any
    try {
      res = await MemberApi.getMemeberCardDetail(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    return res
  }
  // 获取会员优惠券
  async getMemberCoupons () {
    if (memberMobx.memberInfo.memberId === 0) return
    const params = {
      memberId: memberMobx.memberInfo.memberId,
      isUsed: 0,
      isExpire: 0,
    }
    let res: any[]
    try {
      res = await MemberApi.getMemberCoupons(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.couponList = this.getMyCoupon(res))
    })
    return res
  }
  // 优惠码兑换
  async redeemCouponsByCode (code: string) {
    return await MemberApi.redeemCouponsbyPromoCode({ promoCode: code })
  }

  // 获取会员地址
  async getMemberAddress () {
    if (memberMobx.memberInfo.memberId === 0) return
    let res: any[]
    try {
      res = await MemberApi.getAddressList()
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.addressList = res)
    })
    return res
  }
  async deleteMemberAddress (id: number) {
    if (memberMobx.memberInfo.memberId === 0) return
    let res: any[]
    try {
      res = await MemberApi.deleteAddress(id)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.getMemberAddress())
    })
    return res
  }
  async updateMemberAddress (para: any) {
    if (memberMobx.memberInfo.memberId === 0) return
    let res: any
    try {
      res = await MemberApi.updateAddress(para)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.getMemberAddress())
    })
    return res
  }
  async createMemberAddress (para: any) {
    if (memberMobx.memberInfo.memberId === 0) return
    let res: any
    try {
      res = await MemberApi.createAddress(para)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.getMemberAddress())
    })
    return res
  }
  // 获取未领取优惠券
  async getUnacquiredCoupons (autoAcquire: boolean = false) {
    const params = {
      memberId: this.memberInfo.memberId,
    }
    let res: any
    const resArr: any[] = []
    try {
      if (!autoAcquire) {
        res = await MemberApi.getMemberUnacquiredCoupons(params)
        runInAction(() => {
          this.isUnacquiredInit = true
        })
      }
      if (res && res.length > 0) {
        const couponIds: number[] = []
        each(res, (item: any) => {
          each(item.coupons, (cItem: any) => {
            if (!couponIds.includes(cItem.couponId)) {
              couponIds.push(cItem.couponId)
            }
          })
        })
        const couponRes = await ActivityAPI.getCouponsList({
          couponIds: couponIds
        })
        const couponMap = keyBy(couponRes, 'couponId')
        each(res, (item: any) => {
          const obj: any = {
            ...item,
            coupons: []
          }
          each(item.coupons, (cItem: any) => {
            if (couponMap[cItem.couponId]) {
              obj.coupons.push({ ...couponMap[cItem.couponId], ...cItem })
            }
          })
          if (obj.coupons.length > 0) {
            resArr.push(obj)
          }
        })
      }
    } catch (error) { }
    runInAction(() => {
      res && (this.unacquiredCoupons = resArr)
      if (autoAcquire) (this.acquiredCouponList())
    })
    return resArr
  }
  // 领取优惠券
  async acquiredCouponList (para: {
    outType?: string;
    outNos?: string[];
  } = {}) {
    const params = {
      memberId: this.memberInfo.memberId,
      ...para,
    }
    let res: any
    try {
      res = await MemberApi.acquiredCouponList(params)
    } catch (error) { }
    runInAction(() => {
      /** 领券后刷新营销数据 */
      res && this.refreshMemeberBenefits()
      res && this.getMemberCoupons()
      res && (this.unacquiredCoupons = [])
    })
    return res
  }
  // 领取活动优惠券
  async acquireActiveCoupons (activityId: number) {
    const params = {
      activityId,
      storeId: this.storeId,
      memberId: this.memberInfo.memberId,
    }
    let res: any
    try {
      res = await MemberApi.acquireActiveCoupons(params)
    } catch (error) { }
    runInAction(() => {
      /** 领券后刷新营销数据 */
      res && this.refreshMemeberBenefits()
      res && this.getMemberCoupons()
    })
    return res
  }
  async buyRedeemProduct (params: any) {
    let res: any
    try {
      res = await MemberApi.buyRedeemProduct(params)
    } catch (error) {
      return error
    }
    runInAction(() => {
      res && (this.getMemberCards())
    })
    return res
  }
  // 领取会员会员卡
  async acquireMemberCard (cardId: number) {
    const params = {
      memberId: this.memberInfo.memberId,
      cardId,
    }
    let res: any
    try {
      res = await MemberApi.acquireMemberCard(params)
    } catch (error) {
      let message: string = i18n.t('mkt.member.applyAgainTip')
      if (error.code === 4005) {
        message = i18n.t('mkt.member.acquireCardTip1')
      }
      toast({ message })
      return error
    }
    runInAction(() => {
      this.getMemberCards()
    })
    return res
  }
  /**
  * 检测手机、邮箱是否注册
  * @param params
  */
  async checkAccount (params: {
    brandId: string;
    countryCode: number;
    mobile: string;
    email: string;
  }) {
    params.mobile && (params.mobile = params.mobile.trim())
    params.email && (params.email = params.email.trim())
    let res: any
    try {
      res = await MemberApi.checkReg(params)
    } catch (error) { }
    return res
  }
  /**
  * 合并账户
  * @param params
  */
  async merge (params: {
    brandId: string;
    countryCode: number;
    mobile: string;
    email: string;
    captcha: string;
  }) {
    params.mobile && (params.mobile = params.mobile.trim())
    params.email && (params.email = params.email.trim())
    params.captcha && (params.captcha = params.captcha.trim())
    let res: IMemberInfo
    let beneRes: any
    let infoRes: any
    try {
      res = await MemberApi.merge(params)
      await Config.setToken(res.token)
      if (res) {
        beneRes = await MemberApi.getMemeberBenefits()
        infoRes = await MemberApi.getMemeberInfo()
      }
    } catch (error) {
      toast({ message: i18n.t('mkt.member.mergeFail') })
      return error
    }
    runInAction(() => {
      res && (this._token = res.token)
      infoRes && (this.setMemberInfo(infoRes))
      beneRes && (this.noPurchasedCards = beneRes.noPurchasedCards)
      beneRes && (this.cardList = this.getDefaultCardTop(beneRes.memberCards))
      beneRes && (this.cardInfo = this.getBestCard(beneRes.memberCards))
      beneRes && (this.mallCardInfo = this.getBestCard(beneRes.memberCards, true))
      beneRes && (this.couponList = this.getMyCoupon(beneRes.coupons))
      beneRes && (this.couponsConfigMap = beneRes.couponsConfigMap)
      res && beneRes && this.mpcSetMemberInfo(res, this.mpc.eatType === 'mall' ? this.mallCardInfo : this.cardInfo, this.couponList, this.couponsConfigMap)
      beneRes && (this.giftCardList = beneRes.giftCards)
    })
    return res
  }
  /**
  * 绑定手机、邮箱
  * @param params
  */
  async bindingAccount (params: {
    email?: string;
    countryCode?: number;
    mobile?: string;
    captcha?: string;
    invitation?: any;
    loginPwd?: any;
  }) {
    params.email && (params.email = params.email.trim())
    params.mobile && (params.mobile = params.mobile.trim())
    params.loginPwd && (params.loginPwd = md5(params.loginPwd.trim()))
    if (Config.shareInfo) {
      const shareInfoObj: any = JSON.parse(decodeURIComponent(Config.shareInfo))
      params.invitation = shareInfoObj
    }
    let res: any
    try {
      res = await MemberApi.bindAccount(params)
      res && res.isNewMember && (this._isNewMember = res.isNewMember)
      !res && (res = true)
      await this.getMemberInfo()
    } catch (error) {
      if (error?.code === 4004) { // 验证码错误
        toast({ message: i18n.t('checkout.wrongCaptcha') })
        return
      }
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && this.clearShareInfo()
    })
    return res
  }
  /**
  * 微信小程序设置手机号
  * @param params
  */
  async setWechatMiniappPhoneNumber (params: any) {
    if (Config.shareInfo) {
      const shareInfoObj: any = JSON.parse(decodeURIComponent(Config.shareInfo))
      params.invitation = shareInfoObj
    }
    let res: any
    try {
      res = await MemberApi.setWechatMiniappPhoneNumber(params)
    } catch (error) {
      return error
    }
    runInAction(() => {
      if (res?.token) {
        Config.setToken(res.token) // 通过微信快捷登录合并会员时刷新token
        this._token = res.token
      }
      res && this.getMemberInfo()
      res && this.clearShareInfo()
    })
    return res
  }
  /**
  * 重置密码
  * @param params
  * @returns
  */
  async resetPassword (params: {
    mobile?: string;
    countryCode?: number;
    email?: string;
    captcha: string;
    newPwd: string;
    deviceInfo?: {
      deviceId: string;
    };
  }, reLogin: boolean = false) {
    params.newPwd && (params.newPwd = md5(params.newPwd.trim()))
    let res: any
    try {
      res = await MemberApi.changePassword(params)
      const loginPara: any = {
        loginPwd: params.newPwd,
      }
      params.mobile && (loginPara.mobile = params.mobile)
      params.email && (loginPara.email = params.email)
      params.countryCode && (loginPara.countryCode = params.countryCode)
      if (res && (reLogin || !this._token)) {
        await this.login('normal', loginPara, true, false)
      }
    } catch (error) {
      fmtMktMessage(null, error)
    }
    return res
  }
  /**
  * 绑定礼品卡
  * @param params
  * @returns
  */
  async bindingGiftCard (params: any) {
    Object.assign(params, {
      memberId: memberMobx.memberInfo.memberId,
    })
    params.cardNo && (params.cardNo = params.cardNo.replace(/\s/g, ''))
    params.cardPin && (params.cardPin = params.cardPin.trim())
    let res: any
    try {
      res = await MemberApi.bindCard(params)
    } catch (error) {
      toast({ message: i18n.t('mkt.giftCard.invalidCardInfo') })
    }
    return res
  }
  /**
   * 绑定礼品卡
   * @param params
   * @returns
   */
  async aquireMemberGiveCoupon (giveId: number) {
    const params: any = {
      memberId: this.memberInfo.memberId,
      memberCouponGiveId: giveId
    }
    let res: any
    try {
      res = await MemberApi.aquireMemberGiveCoupon(params)
    } catch (error) {
      return error
    }
    runInAction(() => {
      res && this.getMemberCoupons()
    })
    return res
  }
  /**
  * 退出登录
  * @returns
  */
  async logout (reload?: boolean) {
    localStore.remove(this.tokenKey)
    localStore.remove(this.consumeCouponsKey)
    this.clearShareInfo()
    this._token = ''
    this.memberInfo = getDefaultMemberInfo()
    this.memberExtra = defaultMemberExtra
    Config.clearToken()
    mpc.clearMemberInfo()
    this.initData()
    memberCardMobx.initData()
    if (isWeixin && reload && window.bo === 'cn') {
      if (isWeixinMiniProgram) {
        miniAppRouteTo({ url: '/pages/launch/index' })
      } else {
        setTimeout(() => {
          const reloadNum: any = localStore.get(this.reloadNumKey)
          if (reloadNum) {
            if (parseInt(reloadNum) >= 3) {
              localStore.remove(this.reloadNumKey)
            } else {
              localStore.set(this.reloadNumKey, reloadNum + 1)
              window.location.reload()
            }
          } else {
            localStore.set(this.reloadNumKey, 1)
            window.location.reload()
          }
        }, 1000)
      }
    }
    return true
  }
  async initData () {
    this.cardList = []
    this.couponList = []
    this.unacquiredCoupons = []
    this.giftCardList = []
    this.addressList = []
    this.cardInfo = null
    this.mallCardInfo = null
  }
  /**
  * 结算页面添加礼品卡
  * @param {string} cardNo - 卡号
  */
  async checkoutAddGiftCard (cardNo: string): Promise<IMemberGiftCardInfo> {
    const params = {
      type: 'giftcard',
      cardNo,
    }
    params.cardNo && (params.cardNo = params.cardNo.replace(/\s/g, ''))
    let res: any
    try {
      res = await MemberApi.getMemeberCardDetail(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    const handleFilter = (data: any) => {
      const canStore = data?.giftcard?.isAllStore || (memberMobx.storeId && data?.giftcard?.storeIds ? indexOf(data?.giftcard?.storeIds, memberMobx.storeId) > -1 : true)
      return canStore && data.balance > 0 && (data.expireTime === null || ddayjs(data.expireTime).valueOf() > Date.now()) && data.status === 'enable'
    }
    if (res && handleFilter(res)) {
      return res
    } else {
      throw new Error(i18n.t('mkt.common.tipAddGiftCardError'))
    }
  }
  /**
  * 赠送礼品卡
  * @param para
  * @returns
  */
  async giveCard (para: {
    cardNo: string;
    receiverMobile?: string;
    countryCode?: string;
    receiverEmail?: string;
    giverName?: string;
    receiverName?: string;
    message?: string;
    noSendMessage?: boolean;
  }): Promise<IMemberGiftCardInfo> {
    const params: any = {
      ...para,
      type: 'giftcard',
    }
    params.cardNo && (params.cardNo = params.cardNo.replace(/\s/g, ''))
    let res: any
    try {
      res = await MemberApi.giveCard(params)
    } catch (error) {
    }
    runInAction(() => {
      res && this.getMemberGiftCards()
    })
    return res
  }
  /**
  * 获取赠送记录
  * @returns
  */
  async getGiveLogs () {
    let res: any
    try {
      res = await MemberApi.getGiveLogs({
        memberId: this.memberInfo.memberId,
      })
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.giveLogs = res)
    })
    return res
  }
  /**
  * 初始化会员信息
  * @param data
  */
  async initMember (data: any) {
    this._brandId = Config.BRANDID
    this._storeId = Config.STOREID
    const keepSign = localStore.get(`ls.keepSign.${Config.BRANDID}`)
    const sessionKeep = sessionStorage.getItem(`ls.keepSign.${Config.BRANDID}`)
    // console.log(`初始化会员--------BRANDID:${Config.BRANDID} STOREID:${Config.STOREID} keepSign:${keepSign}  sessionKeep:${sessionKeep}`)
    if (keepSign === undefined) {
      localStore.set(`ls.keepSign.${Config.BRANDID}`, 1)
    }
    if (keepSign === undefined || keepSign === 1 || sessionKeep === '1') {
      if (data && data.member && data.member.token) {
        this._token = data.member.token
        Config.setToken(data.member.token)
      } else {
        this._token = Config.token
        if (Config.token) Config.setToken(Config.token)
      }
    } else {
      Config.clearToken()
    }
    if (this._brandId) {
      let res: any
      try {
        res = await MemberApi.getBrandConfig()
      } catch (error) { }
      if (res) {
        runInAction(() => {
          res.clauses = keyBy(res.clauses, 'applyScope')
          this.brandConfig = res
        })
      }
    }
    if (data) {
      runInAction(() => {
        this.memberInfo = this.converMemberInfo(data.member)
        this.memberExtra = {
          ...defaultMemberExtra,
          ...data.member.extra,
        }
        if (Config.posLoginInfo && this._token) {
          if (this.memberInfo.type === 'member') {
            const posLoginObj: any = JSON.parse(decodeURIComponent(Config.posLoginInfo))
            if (ddayjs('brand').valueOf() - posLoginObj.time < 1000 * 60 * 5) {
              MemberApi.qrCodeLogin({
                deviceId: posLoginObj.storeDeviceId,
                timestamp: posLoginObj.time
              })
            } else {
              toast({ message: i18n.t('mkt.member.posLoginTip2') })
            }
          } else {
            toast({ message: i18n.t('mkt.member.posLoginTip1') })
          }
        }
        this.refreshMemeberBenefits()
        /** 获取未领取优惠券 */
        if (memberMobx.unacquiredCoupons.length === 0 && memberMobx.token) {
          memberMobx.getUnacquiredCoupons()
        }
      })
    }
  }
  // 获取会员下所有绑定方式
  async memberAllBind () {
    let res: any
    try {
      res = await MemberApi.getMemberAllBind()
    } catch (error) {
      fmtMktMessage(null, error)
    }
    return res
  }
  // 三方账号解绑
  async unbindChannel (params: any) {
    let res: any
    try {
      res = await MemberApi.unbindChannel(params)
    } catch (error) {
      fmtMktMessage(null, error)
    }
    return res
  }
  // 三方账号绑定
  async bindChannel (params: any) {
    let res: any
    try {
      res = await MemberApi.bindChannel(params)
    } catch (error) {
      if (error.code === 1000) {
        return error
      } else {
        fmtMktMessage(null, error)
      }
    }
    return res
  }
  // 三方信息存储
  public setThirdAuthInfo (info: any) {
    localStore.set(this.thirdAuthInfoKey, info)
  }
  async getActivityState () {
    let res: any
    try {
      res = await MemberApi.getActivityState()
    } catch (error) {
      fmtMktMessage(null, error)
    }
    runInAction(() => {
      res && (this.openState = res)
    })
    return res
  }

  verifyCodeTypes: any = {
    register: 'register', // 注册
    login: 'login', // 登录账号
    binding: 'bind', // 绑定账号
    resetPassword: 'resetPwd', // 重置密码
    merge: 'merge', // 合并密码
  }

  // 检查会员是否有权益
  async checkHasBenefits (params: {
    countryCode: number;
    mobile: string;
  }) {
    let res: any
    try {
      res = await MemberApi.checkHasBenefits(params)
      !res && (res = true)
    } catch (error) { }
    return res
  }

  /**
   * 领取消费赠券
   * @param params
   * @returns
   */
  async acquireByConsumeSendCoupon ({ ordersId, activityId = '' }) {
    const params: any = {
      storeId: this.storeId,
      ordersId,
      activityId
    }
    let res: any
    try {
      res = await MemberApi.acquireByConsumeSendCoupon(params)
    } catch (error) {
      return error
    }
    return res
  }

  // 设置默认会员卡
  async setDefaultCard (params: any) {
    let res: any
    try {
      res = await MemberApi.setDefaultCard(params)
    } catch (error) {
      return error
    }
    runInAction(async () => {
      let list: any
      res && (list = await this.getMemberCards())
      list && (this.cardInfo = this.getBestCard(list))
      list && (this.setCardInfo(this.cardInfo))
    })
    return res
  }
}

const memberStore = new MemberStore()

export default memberStore
